import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { MetricStrip } from '../components/MetricStrip'
import { Pagination } from '../components/Pagination'
import { aggregateSkus, sortDouyinRows, type DouyinSkuAggregate } from '../douyinCommerce'
import { DouyinStatisticsBar, douyinStatisticsWindow, useDouyinStatisticsPeriod } from '../douyinStatistics'
import type { DouyinLiveAnalysis } from '../douyinTypes'
import { integer, money } from '../utils'
import { DouyinBoundary, DouyinProductImage, DySigned, DySortTh, DyTag, dyPct1 } from './douyinCommon'

type LinkFilter = 'all' | 'coded' | 'uncoded'
type SalesFilter = 'all' | 'active' | 'zero'

const SKU_COLUMNS: Array<{ key: string; label: string; left?: boolean; className?: string }> = [
  { key: 'name', label: '商品 / 规格', left: true, className: 'dy-col-sku' },
  { key: 'code', label: '公司产品编码', left: true },
  { key: 'company', label: '公司品名', left: true },
  { key: 'paid', label: '支付金额' },
  { key: 'items', label: '成交件数' },
  { key: 'orders', label: '成交订单' },
  { key: 'refund', label: '退款金额' },
  { key: 'uc', label: '单件成本' },
  { key: 'cost', label: '产品成本' },
  { key: 'gm', label: '货差毛利' },
  { key: 'gmr', label: '货差毛利率' },
  { key: 'price', label: '平台售价' },
  { key: 'stock', label: '抖店库存' },
  { key: 'dyVol', label: '抖店销量' },
  { key: 'linked', label: '关联状态' },
]

export function DouyinSkuListPage() {
  return <DouyinBoundary>{(data) => <DouyinSkuListContent data={data} />}</DouyinBoundary>
}

function DouyinSkuListContent({ data }: { data: DouyinLiveAnalysis }) {
  const [period, setPeriod] = useDouyinStatisticsPeriod('day')
  const window = douyinStatisticsWindow(data.meta, period)
  const [search, setSearch] = useState('')
  const [link, setLink] = useState<LinkFilter>('all')
  const [sales, setSales] = useState<SalesFilter>('all')
  const [sort, setSort] = useState({ key: 'paid', desc: true })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const all = useMemo(
    () => window ? aggregateSkus(data, window) : [],
    [data, window?.startDate, window?.endDate], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const activeCount = all.filter((row) => row.active).length
  const paidTotal = all.reduce((sum, row) => sum + row.paid, 0)
  const costTotal = all.reduce((sum, row) => sum + (row.cost || 0), 0)
  const codedPaid = all.reduce((sum, row) => sum + (row.code ? row.paid : 0), 0)
  const grossMargin = paidTotal - costTotal

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return sortDouyinRows(all.filter((row) => {
      if (link === 'coded' && !row.code) return false
      if (link === 'uncoded' && row.code) return false
      if (sales === 'active' && !row.active) return false
      if (sales === 'zero' && row.active) return false
      if (!needle) return true
      return row.n.toLowerCase().includes(needle)
        || row.name.toLowerCase().includes(needle)
        || (row.code || '').toLowerCase().includes(needle)
        || (row.company || '').toLowerCase().includes(needle)
    }), sort, (row) => row.n)
  }, [all, link, sales, search, sort])

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const toggleSort = (key: string) => {
    setSort((current) => ({ key, desc: current.key === key ? !current.desc : true }))
    setPage(1)
  }

  return <div className="page-stack dy-page">
    <div className="page-title-row">
      <div><span className="dy-platform-label">DOUYIN LIVE</span><h1>SKU商品</h1><p>共 {integer(all.length)} 个SKU · 动销 {integer(activeCount)} 个</p></div>
    </div>
    <DouyinStatisticsBar period={period} onChange={(value) => { setPeriod(value); setPage(1) }} window={window} />
    <div className="pdd-caliber-note">口径说明：当前销量仅覆盖直播间成交（数据源为罗盘直播明细，{integer(data.sessions.length)}场），商城/短视频/橱窗等渠道尚未接入，不代表全店销量；货差毛利 = 用户支付金额 − 公司产品成本，运费/扣点/税/佣金/千川在场次与日级扣除，不摊到商品行；未接编码的金额不摊成本，单列「缺成本」。「抖店销量」来自商品导出，导出未注明时间口径，仅作参考对照。商品底表为抖店商品库全量（含无直播销量商品）；销量口径仍仅覆盖直播间成交。</div>
    <MetricStrip items={[
      { label: '成交金额', value: money(paidTotal), context: '窗口内直播间成交' },
      { label: '货差毛利', value: money(grossMargin), context: '仅已接编码部分摊成本' },
      { label: '成交件数', value: integer(all.reduce((sum, row) => sum + row.items, 0)) },
      { label: '成交订单', value: integer(all.reduce((sum, row) => sum + row.orders, 0)) },
      { label: '退款金额', value: money(all.reduce((sum, row) => sum + row.refund, 0)) },
      { label: '已接编码金额占比', value: dyPct1(paidTotal > 0 ? codedPaid / paidTotal : null) },
    ]} />
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>SKU筛选</h2><p>规格、编码或公司品名</p></div><span>命中 {integer(filtered.length)} 个 · 动销 {integer(filtered.filter((row) => row.active).length)} 个</span></div>
      <div className="filter-bar">
        <label className="search-control"><Search aria-hidden="true" /><input value={search} placeholder="搜索规格 / 编码 / 公司品名" onChange={(event) => { setSearch(event.target.value); setPage(1) }} /></label>
        <label><span className="sr-only">关联状态</span><select value={link} onChange={(event) => { setLink(event.target.value as LinkFilter); setPage(1) }}>
          <option value="all">关联状态：全部</option>
          <option value="coded">已接编码</option>
          <option value="uncoded">未接编码</option>
        </select></label>
        <label><span className="sr-only">销量筛选</span><select value={sales} onChange={(event) => { setSales(event.target.value as SalesFilter); setPage(1) }}>
          <option value="all">销量：全部</option>
          <option value="active">仅动销</option>
          <option value="zero">无销量</option>
        </select></label>
      </div>
    </section>
    <section className="overview-section">
      <div className="table-responsive"><table className="product-table dy-table dy-sku-table"><thead><tr>
        {SKU_COLUMNS.map((column) => <DySortTh key={column.key} columnKey={column.key} label={column.label} left={column.left} className={column.className} sort={sort} onSort={toggleSort} />)}
      </tr></thead><tbody>{visible.map((row) => <DouyinSkuRow key={row.key} row={row} />)}</tbody></table></div>
      <p className="dy-table-footnote">共 {integer(filtered.length)} 个SKU（动销 {integer(filtered.filter((row) => row.active).length)} 个）· 支付合计 {money(filtered.reduce((sum, row) => sum + row.paid, 0))}；SKU 合计比 SPU 口径多 ¥103.00，为平台导出自身差异（疑似福袋，D-206 已登记），不修数。</p>
      <Pagination page={currentPage} pageCount={pageCount} total={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />
    </section>
  </div>
}

function DouyinSkuRow({ row }: { row: DouyinSkuAggregate }) {
  return <tr>
    <td className="l dy-col-sku">
      <div className="product-link">
        <DouyinProductImage src={row.img} />
        <span className="dy-prod-main">
          <strong title={row.name}>{row.name}</strong>
          <small>
            <span>{row.n}</span>
            {row.noCatalog ? <DyTag kind="off">未在商品库</DyTag> : null}
          </small>
        </span>
      </div>
    </td>
    <td className="l">{row.code ? <b>{row.code}</b> : '—'}</td>
    <td className="l dy-ell" title={row.company || undefined}>{row.company || '—'}</td>
    <td>{row.active ? money(row.paid) : <span className="dy-faint">¥0.00</span>}</td>
    <td>{row.active ? integer(row.items) : <span className="dy-faint">0</span>}</td>
    <td>{row.active ? integer(row.orders) : <span className="dy-faint">0</span>}</td>
    <td>{row.refund ? money(row.refund) : '—'}</td>
    <td>{row.uc != null ? `${money(row.uc)}/件` : '—'}</td>
    <td>{row.cost != null ? money(row.cost) : '—'}</td>
    <td>{row.gm == null ? '—' : <DySigned value={row.gm} />}</td>
    <td>{dyPct1(row.gmr)}</td>
    <td>{row.price != null ? money(row.price) : '—'}</td>
    <td>{row.stock != null ? integer(row.stock) : '—'}</td>
    <td className="dy-faint">{row.dyVol != null ? integer(row.dyVol) : '—'}</td>
    <td>{row.code ? <DyTag kind="coded">已接编码</DyTag> : <DyTag kind="uncoded">未接编码</DyTag>}</td>
  </tr>
}
